import React, { useRef } from "react";
import classNames from "classnames";
import { QuizBody } from "components/QuizBody/QuizBody";
import { QuizHeader } from "components/QuizHeader/QuizHeader";
import { Timer } from "components/Timer/Timer";
import {
  API_SAVE_AUDIO_EVENT,
  API_SAVE_MCQ_EVENT,
  FUNDAMENTAL_QUIZ,
  NEW_DATA_EVENT,
  NOT_REQUIRED,
  ORGANIZATIONS,
  PAUSE_TIMER_EVENT,
  QUESTION_PREVIEW_EVENT,
  QUESTION_TYPE,
  RESUME_TIMER_EVENT,
} from "constant";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";
import { GC } from "services/gamecentreService";
import { storage } from "services/storage";
import {
  addCountExcludingBreaker,
  askForMicrophonePermission,
  getQuestionsExcludingBreaker,
} from "utils/helper";
import Quiz from "views/Quiz";
import SplashScreen from "views/SectionSplashScreen";
import "./index.css";
import { PermissionsModal } from "components/modals/PermissionsModal/PermissionsModal";
import { Chat } from "views/Quiz/Chat";
import { ChatHeader } from "components/Chat/ChatHeader/ChatHeader";
import { AudioTestingModal } from "components/modals/AudioTestingModal/AudioTestingModal";
import EndChatButton from "components/Chat/EndChatBtn/EndChatBtn";
import StartChatBtn from "components/Chat/StartChatBtn/StartChatBtn";
import SpotlightAudioQuiz from "components/QuizBody/Spotlight/Speech/SpotlightAudioQuiz";
import SpotlightMCQ from "components/QuizBody/Spotlight/MCQ/SpotlightMCQ";
import MultiChatQuiz from "components/Chat/MultiChat/MultiChat";
import { Loader } from "components/modals/Loader/Loader";

const inIframe = window.location !== window.parent.location;

export const Home = () => {
  const [section, setSection] = useState(null);
  const [activeQuestionId, setActiveQuestionId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isQuizEnded, setIsQuizEnded] = useState(false);
  const [permissionPopup, setPermissionPopup] = useState(false);
  const [audioTestingVisible, setAudioTestingVisible] = useState(false);

  // Will be equal to NOT_REQUIRED in case no speech based question otherwise boolean
  const [haveAudioPermission, setHaveAudioPermission] = useState(false);

  // Chat component state (uplifted to send when timer expires);
  const [messages, setMessages] = useState([]);

  // this keep the total time for the question
  // it could be overall quiz time or a specific quesion time
  const [timeLeft, setTimeLeft] = useState(0);
  const [pauseTimer, setPauseTimer] = useState(false);
  const [chatStarted, setChatStarted] = useState(false)
  const [multiChatQuiz, setMultiChatQuiz] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isOffline, setIsOffline] = useState(false)
  const chatTimerInterval = useRef()
  const quizTimerInterval = useRef()
  const scrollRef = useRef(null)

  const allowSubmitChat = messages && messages.length >= 3;
  const organization = storage.get.org();
  const spotlightOrgFlag = organization === ORGANIZATIONS.SPOTLIGHT

  const questionsCount = useMemo(
    () => getQuestionsExcludingBreaker(section?.questions).length,
    [section]
  );

  // ===================
  // functions
  // ===================

  const checkPermissionHandler = async (data) => {
    const checkPermission = await askForMicrophonePermission(data?.questions);
    if (!checkPermission) {
      setPermissionPopup(true);
      window.dispatchEvent(new Event(PAUSE_TIMER_EVENT))
    } else {
      setHaveAudioPermission(checkPermission);
    }
  };

  const syncData = () => {
    // This function gets called whenever there is new event dispatches after setting new data
    const data = storage.get.quizData();

    // Check wizrFlag add custom font-family "Patron"
    const organization = storage.get.org();
    const wizrFlag = organization === ORGANIZATIONS.WIZR;
    if (wizrFlag) document.body.style.fontFamily = "Patron";
    setQuizData(data);
  };

  // Called on event QUESTION_PREVIEW_EVENT -> Bypass quiz splash -> Onto question
  const syncDataForQuesPreview = () => {
    const data = storage.get.quizData();
    setActiveQuestionId(data?.questions[0]?.id);
    setSection(data);
    setIsLoading(false);
    setTimeLeft(null);
  };

  const setQuizData = async (data) => {
    if (data) {
      const chatQuestions = data?.questions?.filter(ques => ques?.type === QUESTION_TYPE.CHAT)
      if (chatQuestions?.length === data?.questions?.length && chatQuestions?.length > 1) {
        setMultiChatQuiz(true)
      }
      // adding count in each question
      data.questions = addCountExcludingBreaker(data.questions);

      if (data.lastQuestionId) {
        // Set the next question details if last question id is present
        const lastQuestionIndex = data.questions.findIndex(
          (question) => question.id === data.lastQuestionId
        );

        if (lastQuestionIndex < data.questions.length - 1) {
          const nextQuestion = data.questions[lastQuestionIndex + 1];
          setActiveQuestionId(nextQuestion.id);

          let timer = null;
          if (data.timer) {
            timer = data.timer - (data.totalTimeSpent ?? 0);
          } else if (nextQuestion.timer) {
            timer = nextQuestion.timer;
          }
          /**
           * Get time left from LS and update timeLeft to resume quiz timer, except chat. 
           */
          const lastTimeLeft = storage.get.timeRemaining()
          if (nextQuestion.type === QUESTION_TYPE.CHAT) {
            setTimeLeft(timer)
          } else {
            setTimeLeft(lastTimeLeft ?? timer);
          }
        } else {
          // Quiz has already been completed for this user
          // Show end screen
          quizEndHandler();
        }
      }

      setSection(data);
      setIsLoading(false);

      // checking for audio recording permissions
      checkPermissionHandler(data);
    }
  };

  const continueHandler = () => {
    const { questions } = section;
    if (questions.length > 0) {
      const [firstQuestion] = questions;

      if (section.timer) {
        setTimeLeft(section.timer - (section.totalTimeSpent ?? 0));
      } else {
        setTimeLeft(firstQuestion.timer);
      }
      if (spotlightOrgFlag) {
        setActiveQuestionId(firstQuestion.id);
        GC.createGameMasterSession();
      }
      // Need check here as well since user can skip onto questions without interacting with perm prompt
      else if (haveAudioPermission) {
        setActiveQuestionId(firstQuestion.id);
        GC.createGameMasterSession();
      }
      else checkPermissionHandler(section);
    } else quizEndHandler();
  };

  const moveToNextGame = () => {
    let timeSpent;
    if (section.timer) {
      // calculating timeSpent only when overall quiz time is present
      timeSpent = section.timer - timeLeft;
    }

    inIframe && GC.sendGameEndMessage(timeSpent);
  };

  const quizEndHandler = () => {
    setIsQuizEnded(true);
    // Temp measure -> Destroy FT cache
    storage.destroy.freeTextCache()
  };

  const handleStartChat = () => {
    setChatStarted(true)
    chatTimerInterval.current = setInterval(() => {
      setTimeLeft((ps) => ps - 1)
    }, 1000)
  };

  const setDefaultQuizData = async () => {
    const params = new URLSearchParams(window.location.search);
    const quizId = params.get("quiz-id");

    if (quizId) {
      try {
        const host = process.env.REACT_APP_API_ENDPOINT;
        // const headers = _AuthHeaders();
        let res = await fetch(
          `${host}/api/quiz/v1/${quizId}/questionsWithoutAuth`
        );
        if (!res.ok) throw Error(`API error code: ${res.status}`);

        res = await res.json();
        const { success, quiz, ...rest } = res;
        if (success) {
          setQuizData({ ...quiz, ...rest });
        }
      } catch (err) {
        console.error("Error Occured: ", err);
        setQuizData(FUNDAMENTAL_QUIZ);
      }
    } else {
      setQuizData(FUNDAMENTAL_QUIZ);
    }
  };

  // ===================
  // useCallbacks
  // ===================
  const setNextQuestion = useCallback(() => {
    // clearing last chat messages
    setMessages([]);

    const { questions } = section;
    const currentQuestionIndex = questions.findIndex(
      (question) => question.id === activeQuestionId
    );

    if (currentQuestionIndex < questions.length - 1) {
      const nextQuestion = questions[currentQuestionIndex + 1];
      const nextQuestionId = nextQuestion.id;
      setActiveQuestionId(nextQuestionId);

      if (!section?.timer) {
        nextQuestion.timer && setTimeLeft(nextQuestion.timer);
      } else if (
        section?.timer &&
        nextQuestion.type === QUESTION_TYPE.BREAKER_SCREEN
      ) {
        setPauseTimer(true);
      } else {
        setPauseTimer(false);
      }
    } else {
      quizEndHandler();
    }
  }, [activeQuestionId, section, section?.id, pauseTimer]);

  const optionSubmitHandler = useCallback(
    (
      {
        optionId,
        answerText,
        // mediaFile,
        mediaId,
        customText,
        messages,
        promptId,
        finish,
        isChat = false,
      },
      nextQuestion = true,
      markQuizComplete = false,
      isMCQ = false
    ) => {
      const saveDataPayload = {
        optionId,
        answerText,
        // mediaFile,
        mediaId,
        customText,
        questionId: activeQuestionId,
        quizId: section.id,

        // chat related props
        messages,
        promptId,
        finish,

        // Key to send event back to Quiz -> Proceed to next question
        isMCQ
      };
      if (section.timer) {
        // saving timeSpent only when overall quiz time is present
        saveDataPayload.timeSpent = section.timer - timeLeft;
      }
      if (markQuizComplete) saveDataPayload.markQuizComplete = true;
      const currentQuestionIndex = section?.questions?.findIndex(
        (question) => question?.id === activeQuestionId
      )
      if (currentQuestionIndex == (section?.questions?.length - 1)) saveDataPayload.isLastQuestion = true

      if (isChat) saveDataPayload.isChat = true
      // send selected option data to the api
      inIframe && GC.sendGameDataSaveMessage(saveDataPayload);

      if (inIframe) {
        // Handling next question call based on save event from GC.
        // See window.addEventListener(API_SAVE_MCQ_EVENT, setNextQuestion);
        return;
      }
      nextQuestion && setNextQuestion();
    },
    [activeQuestionId, section?.id, section?.timer, setNextQuestion, timeLeft]
  );

  const submitCloseChat = useCallback(() => {
    optionSubmitHandler(
      { answerText: JSON.stringify(messages), isChat: true, finish: true },
      true
    );
    setIsSubmitting(true)
  }, [optionSubmitHandler]);

  const handleBlur = () => {
    GC.sendGameBlurredMessage()
  }

  // ===================
  // useEffects
  // ===================

  // Skip intro screen when loaded for wizr sdk
  useEffect(() => {
    if (section?.id) {
      const wizrFlag = organization === ORGANIZATIONS.WIZR;
      if (wizrFlag) continueHandler()
    }
  }, [section])

  useEffect(() => {
    // timer is not set so not doing anything
    if (timeLeft === null || pauseTimer) {
      return;
    }
    let activeQuestion
    // time's up!!
    if (timeLeft === 0) {
      if (section && activeQuestionId) {
        activeQuestion = section.questions.find(
          (question) => question.id === activeQuestionId
        );
        if (activeQuestion.type === QUESTION_TYPE.CHAT && !multiChatQuiz) {
          return submitCloseChat();
        }

        if (section.timer) {
          optionSubmitHandler({}, false, true);
          quizEndHandler();
        }
      }

      return;
    }
    activeQuestion = section.questions.find(
      (question) => question.id === activeQuestionId
    )
    /**
    * Not for chat quiz, timer will start once user starts the quiz.
    * Updated the check for question type to avoid chat question using this timer (as this useEffect
      has timeLeft as dependency due to which it was running in chat too).
    * Replaced setInterval with setTimeout as this useEffect has timeLeft as dependency, so 
      setTimeout will do.  
    */
    if (!(activeQuestion?.type === QUESTION_TYPE.CHAT)) {
      // Initiating timer
      quizTimerInterval.current = setTimeout(() => {
        setTimeLeft((ps) => {
          storage.set.timeRemaining(ps - 1)
          return ps - 1
        });
      }, 1000);

      return () => {
        clearTimeout(quizTimerInterval.current);
      };
    }
  }, [
    timeLeft,
    optionSubmitHandler,
    section,
    activeQuestionId,
    pauseTimer,
    submitCloseChat
  ]);

  useEffect(() => {
    if (
      haveAudioPermission &&
      haveAudioPermission !== NOT_REQUIRED &&
      !storage.get.audioDevice()
      && !activeQuestionId
    ) {
      setAudioTestingVisible(true);
    }
  }, [haveAudioPermission, activeQuestionId]);

  useEffect(() => {
    if (!inIframe) {
      // Adding timeout to handle React.18 intentinonal re-render on development
      const timeout = setTimeout(setDefaultQuizData, 0);
      return () => clearTimeout(timeout);
    }

    window.addEventListener(NEW_DATA_EVENT, syncData);
    window.addEventListener(QUESTION_PREVIEW_EVENT, syncDataForQuesPreview);
    window.addEventListener("blur", handleBlur)

    return () => {
      window.removeEventListener(NEW_DATA_EVENT, syncData);
      window.removeEventListener(QUESTION_PREVIEW_EVENT, syncDataForQuesPreview);
      window.removeEventListener("blur", handleBlur)
    };
  }, []);

  useEffect(() => {
    const handleUserAgentOffline = () => {
      setIsOffline(true)
      clearInterval(quizTimerInterval.current)
      clearInterval(chatTimerInterval.current)
    }

    const handleUserAgentOnline = () => {
      setIsOffline(false)
      if (timeLeft && timeLeft > 0) {
        const currentQuestion = section?.questions?.find(
          (question) => question.id === activeQuestionId
        )
        /**
         * For chat questions, start the interval again. For other questions,
         * updating only timeLeft would start the interval as setInterval is inside
         * a useEffect with timeLeft dependency. 
         */
        if (currentQuestion?.type === QUESTION_TYPE.CHAT) {
          chatTimerInterval.current = setInterval(() => {
            setTimeLeft(s => s - 1)
          }, 1000)
        } else {
          setTimeLeft(s => s - 1)
        }
      }
    }

    /**
    * Handle audio submissions separately -> Post message comms
    * 
    * =======  UPDATE 30/04/2024 =======
    * Audio submission handler shifted: QuesitonInput.js -> Home.js
    * When game timer expires, 
    * eventListener for checking audio save and then further submitting answer with mediaId was tied to QuestionInput comp,
    * which got unmounted -> Hence no submitAnswer calls.
    * 
    * 
    * === FIX ^-^ ===
    * Move eventListener check on Home.js,
    * now even when gametimer expires, QuestionInput is unmounted,
    * we can still make final submitAnswer call with mediaId received from audio save call.
    *  */
    const audioResponseMessageHandler = (e) => {
      if (!inIframe) {
        // Not calling gamecenter apis if not loaded in it or not speech Q's
        return;
      }
      const { mediaId } = e.detail;
      if (mediaId) optionSubmitHandler({ mediaId }, true, false, true);
    };

    // * Events for listening response from GC
    // ** This will get dispatch from messageEventHandler function inside gamecentreService.js file
    window.addEventListener(PAUSE_TIMER_EVENT, handleUserAgentOffline);
    window.addEventListener(RESUME_TIMER_EVENT, handleUserAgentOnline);
    window.addEventListener(API_SAVE_AUDIO_EVENT, audioResponseMessageHandler);
    window.addEventListener(API_SAVE_MCQ_EVENT, setNextQuestion);

    return () => {
      window.removeEventListener(PAUSE_TIMER_EVENT, handleUserAgentOffline);
      window.removeEventListener(RESUME_TIMER_EVENT, handleUserAgentOnline);
      window.removeEventListener(API_SAVE_AUDIO_EVENT, audioResponseMessageHandler);
      window.removeEventListener(API_SAVE_MCQ_EVENT, setNextQuestion);
    }
  }, [activeQuestionId, section, pauseTimer])

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
      scrollRef.current.scrollLeft = 0;
    }
  }, [activeQuestionId]);

  /**
   * For spotlight speech quiz, skip intro splash screen.
   */
  useEffect(() => {
    if (section?.questions) {
      if (spotlightOrgFlag && [
        QUESTION_TYPE.SPEECH_SCRIPTED,
        QUESTION_TYPE.SPEECH_SPONTANEOUS,
      ].includes(section?.questions?.[0]?.type)) {
        continueHandler()
      }
    }
  }, [section])

  return (
    <div
      className={classNames(
        "relative h-full",
        "overflow-hidden sm:p-4",
        {
          "bg-black": (activeQuestionId && !isQuizEnded) || ((!activeQuestionId || isQuizEnded) && spotlightOrgFlag),
          "bg-[#292421]": (!activeQuestionId || isQuizEnded) && !spotlightOrgFlag,
        }
      )}
    >
      {
        activeQuestionId && !isQuizEnded &&
        <>
          <div className="absolute bottom-[-100px] left-[-350px] w-[800px] h-[250px] rounded-[800px] bg-[#ac95cf] blur-[256px]" />
          <div className="absolute top-[-100px] right-[-350px] w-[800px] h-[250px] rounded-[800px] bg-[#ac95cf] blur-[256px]" />
        </>
      }
      <Loader loading={isLoading} />
      <AnimatePresence>
        <motion.div
          ref={scrollRef}
          className="scroll-container relative h-full overflow-y-auto sm:[overflow-y:unset]">
          {
            spotlightOrgFlag && [
              QUESTION_TYPE.SPEECH_SCRIPTED,
              QUESTION_TYPE.SPEECH_SPONTANEOUS,
            ].includes(section?.questions?.[0]?.type) ?
              activeQuestionId
                ? <SpotlightAudioQuiz
                  questions={section.questions}
                  activeQuestionId={activeQuestionId}
                  sectionName={`${section.title}`}
                  timeLeft={timeLeft}
                  optionSubmitHandler={optionSubmitHandler}
                  questionsCount={questionsCount}
                  isQuizEnded={isQuizEnded}
                  moveToNextGame={moveToNextGame}
                />
                : <></>
              : isQuizEnded ? (
                <SplashScreen
                  heading="You have completed this section"
                  description="Please click on Continue button below to proceed further."
                  onContinue={moveToNextGame}
                  isQuizEnded={isQuizEnded}
                  isMcq={section?.questions?.[0]?.type === QUESTION_TYPE.MCQ}
                />
              ) : section?.id && !activeQuestionId ? (
                <SplashScreen
                  key={section.id}
                  name={`Section`}
                  heading={section.title}
                  description={section.description}
                  onContinue={continueHandler}
                  questionsCount={questionsCount}
                  quizTime={section.timer}
                  imageUrl={section?.media?.link}
                  isMcq={section?.questions?.[0]?.type === QUESTION_TYPE.MCQ}
                  multiChatQuiz={multiChatQuiz}
                />
              ) : activeQuestionId
                ? (multiChatQuiz)
                  ? <MultiChatQuiz
                    questions={section.questions}
                    timer={section.timer}
                    quizId={section.id}
                    quizEndHandler={quizEndHandler}
                  /> : (
                    section.questions.map((question) =>
                      question.id === activeQuestionId ? (
                        <AnimatePresence key={question.id}>
                          {question?.type === QUESTION_TYPE.BREAKER_SCREEN ? (
                            <SplashScreen
                              key={question.id}
                              {...question}
                              sectionBreaker
                              imageUrl={question?.media?.[0]?.mediaUrl}
                              optionSubmitHandler={optionSubmitHandler}
                            />
                          ) : (question?.type === QUESTION_TYPE.MCQ && spotlightOrgFlag)
                            ? <SpotlightMCQ
                              {...question}
                              questionsCount={questionsCount}
                              time={timeLeft}
                              optionSubmitHandler={optionSubmitHandler}
                              isOffline={isOffline}
                            />
                            : (
                              <Quiz key={question.id} id={question.id} chatStarted={chatStarted} isChatQuestion={question?.type === QUESTION_TYPE.CHAT}>
                                {
                                  question?.type === QUESTION_TYPE.CHAT
                                    ? (
                                      <>
                                        <ChatHeader
                                          sectionName={`${section.title}`}
                                          totalTime={
                                            section.timer ? section.timer : question.timer
                                          }
                                          timeLeft={timeLeft}
                                          chatStarted={chatStarted}
                                        >
                                          {
                                            chatStarted ?
                                              <div className="flex-1 text-right">
                                                <EndChatButton allowSubmitChat={(allowSubmitChat && !isSubmitting)} submitCloseChat={submitCloseChat} />
                                              </div> : <div className="flex-1 text-right">
                                                <StartChatBtn handleStartChat={handleStartChat} />
                                              </div>
                                          }
                                        </ChatHeader>
                                        <Chat
                                          {...question}
                                          messages={messages}
                                          setMessages={setMessages}
                                          onChatSubmit={() => {
                                            setIsSubmitting(false)
                                            quizEndHandler()
                                          }}
                                          chatStarted={chatStarted}
                                          handleStartChat={handleStartChat}
                                          totalTime={section.timer ? section.timer : question.timer}
                                          timeLeft={timeLeft}
                                          endChatBtn={<EndChatButton allowSubmitChat={(allowSubmitChat && !isSubmitting)} submitCloseChat={submitCloseChat} />}
                                          isSubmitting={isSubmitting}
                                        />
                                      </>
                                    ) : (
                                      <>
                                        <QuizHeader
                                          type={question.type}
                                          sectionName={`${section.title}`}
                                          questionOrder={`Question ${question.count}/${questionsCount}`}
                                        >
                                          <Timer time={timeLeft} type={question.type} />
                                        </QuizHeader>
                                        <QuizBody
                                          {...question}
                                          time={timeLeft}
                                          optionSubmitHandler={optionSubmitHandler}
                                          isOffline={isOffline}
                                          setPauseTimer={setPauseTimer}
                                        />
                                      </>
                                    )}
                              </Quiz>
                            )}
                        </AnimatePresence>
                      ) : null
                    )
                  ) : null}
        </motion.div>
        <PermissionsModal openPopup={permissionPopup} />
        <AudioTestingModal
          visible={audioTestingVisible}
          onSuccess={() => setAudioTestingVisible(false)}
        />
      </AnimatePresence>
    </div>
  );
};
